/**
 * SurfaceToolBase - Base class for surface-related tools
 *
 * Provides common functionality for tools that work with surface selections:
 * - Surface selection event handling
 * - Surface plane tracking and isolation
 * - View switching to surface plane (aligns camera to look perpendicular to surface)
 * - Isolation plane creation (clips point cloud to show only near the surface)
 *
 * Subclasses (AutoSurfaceTool, future surface tools) should:
 * 1. Call super.onEnable() and super.onDisable()
 * 2. Override onSurfaceChanged() to react to surface selection changes
 * 3. Access surface data via protected properties (currentSurface, etc.)
 */

import { BaseTool } from '../../BaseTool';
import { Viewer } from '../../../viewer/viewer';
import { IPersona } from '../../types';
import { PointoramaSurface, SurfacePlane } from '../../../utils/PointoramaSurface';
import { SurfacePersona } from '../../personas/implementations/SurfacePersona';
import { PointoramaPlane } from '../../../utils/PointoramaPlane';
import { ClipTask, CameraMode } from '../../../defines';
import * as THREE from 'three';
import { getGlobalPropertyService, PropertyType, getSurfacePlaneRotationProperty } from '@pointorama/potree-shared';
import type { Subscription } from '@pointorama/potree-shared';
import { CameraAlignmentTool } from '../../../utils/CameraAlignmentTool';
import type { RenderViewState } from '../../../viewer/RenderViewState';

export abstract class SurfaceToolBase extends BaseTool {
  // Surface isolation state
  protected currentSurface: PointoramaSurface | null = null;
  protected currentSurfacePlane: SurfacePlane | null = null;
  protected currentSurfaceIdentifier: string | null = null;

  // Isolation plane for clipping the point cloud near the surface
  protected surfaceIsolationPlane: PointoramaPlane | null = null;

  // Default tolerance for surface isolation (how thick the slice is)
  protected static readonly DEFAULT_ISOLATION_TOLERANCE = 0.1; // 50cm on each side

  // Reactive property subscription for tolerance
  private toleranceSubscription: Subscription | null = null;

  // Reactive property subscription for rotation (to sync camera roll in 2D mode)
  private rotationSubscription: Subscription | null = null;

  // Subscription to the widget view's currentViewId, so we re-align the
  // widget whenever the user switches it into the surface xray view.
  private widgetViewIdSubscription: Subscription | null = null;

  // Camera alignment tool for rotating the view in 2D mode
  protected cameraAlignmentTool: CameraAlignmentTool;

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);
    this.cameraAlignmentTool = new CameraAlignmentTool(viewer);
  }

  /**
   * Get the SurfacePersona instance (type-safe accessor)
   */
  private get surfacePersona(): SurfacePersona {
    return this.persona as SurfacePersona;
  }

  protected onEnable(): void {
    // Reset surface state
    this.currentSurfaceIdentifier = null;

    // Get initial surface selection from persona
    const surfacePersona = this.persona as SurfacePersona;
    const selectedSurface = surfacePersona.getSelectedSurface();
    const selectedSurfaceIdentifier = surfacePersona.getSelectedSurfaceIdentifier();

    if (selectedSurface && selectedSurfaceIdentifier) {
      this.currentSurfaceIdentifier = selectedSurfaceIdentifier;
      this.currentSurface = selectedSurface;
      this.currentSurfacePlane = selectedSurface.getPlane();

      // Set up isolation plane immediately
      this.updateSurfaceIsolation(selectedSurface.getPlane());

      // Subscribe to tolerance property changes
      this.subscribeToToleranceProperty(selectedSurfaceIdentifier);

      // Subscribe to rotation property changes (syncs camera roll in 2D mode)
      this.subscribeToRotationProperty(selectedSurfaceIdentifier);

      // Align AFTER any pending view switch fully lands. The tool is enabled
      // before BasePersona queues its view-config switch; whenSettled()
      // waits out that switch (and resolves promptly when none is pending).
      // Replaces the old setTimeout(0) that raced the pipeline's awaits —
      // "tool writes last" is now guaranteed by ordering, not timing.
      void this.viewer.personaManager?.viewManager.whenSettled().then(() => {
        if (this.enabled && this.currentSurfacePlane) {
          const alreadyAligned = this.surfacePersona.hasAlignedToCurrentSurface();
          if (alreadyAligned) {
            // Already aligned to this surface (switching between surface tools)
            // Only update orientation, don't center the camera
            this.alignViewToSurface(this.currentSurfacePlane, false);
          } else {
            // First time aligning to this surface - align orientation and center
            this.alignViewToSurface(this.currentSurfacePlane, true);
            this.surfacePersona.setAlignedToCurrentSurface(true);
          }
        }
      });

      this.onSurfaceChanged(selectedSurface, selectedSurfaceIdentifier);
    }

    // Listen for selection changes from persona
    this.persona.addEventListener('surface_selection_changed', this.handleSelectionChanged);

    // Listen for view changes (via ViewManager)
    this.viewer.personaManager?.viewManager.addEventListener('view_will_change', this.handleViewWillChange);
    this.viewer.personaManager?.viewManager.addEventListener('view_changed', this.handleViewChanged);
    this.viewer.personaManager?.viewManager.addEventListener('view_settled', this.handleViewSettled);

    // Watch the widget's own view switcher so we can re-align it whenever
    // the user lands back on the surface xray view (the widget's applyView
    // would otherwise pin to the view's declared default pose).
    this.subscribeToWidgetViewId();
  }

  protected onDisable(): void {
    // Remove selection listener
    this.persona.removeEventListener('surface_selection_changed', this.handleSelectionChanged);
    this.viewer.personaManager?.viewManager.removeEventListener('view_will_change', this.handleViewWillChange);
    this.viewer.personaManager?.viewManager.removeEventListener('view_changed', this.handleViewChanged);
    this.viewer.personaManager?.viewManager.removeEventListener('view_settled', this.handleViewSettled);

    // Unsubscribe from tolerance property
    this.unsubscribeFromToleranceProperty();

    // Unsubscribe from rotation property
    this.unsubscribeFromRotationProperty();

    // Unsubscribe from widget view switcher
    this.unsubscribeFromWidgetViewId();

    // Clean up isolation plane
    this.clearSurfaceIsolationPlane();

    // Cancel camera alignment if active
    this.cameraAlignmentTool.cancel();

    // Clear surface state
    this.currentSurface = null;
    this.currentSurfacePlane = null;
    this.currentSurfaceIdentifier = null;
  }

  /**
   * Handle view WILL change - save 2D camera state BEFORE the view switch happens
   * This is called before any camera modifications by the ViewManager
   */
  private handleViewWillChange = (event: { previousView: { cameraMode?: number } | null; nextView: { cameraMode?: number } | null }): void => {
    // Save 2D camera state if we're currently in 2D mode and switching to 3D
    const previousView = event.previousView;
    const nextView = event.nextView;

    const wasIn2D = previousView?.cameraMode === CameraMode.ORTHOGRAPHIC;
    const willBe2D = nextView?.cameraMode === CameraMode.ORTHOGRAPHIC;

    if (wasIn2D && !willBe2D) {
      // Leaving 2D mode - save the current camera state BEFORE it gets modified
      const view = this.viewer.scene.view;
      this.surfacePersona.save2DCameraState({
        x: view.position.x,
        y: view.position.y,
        z: view.position.z,
        radius: view.radius,
      });
    }
  };

  /**
   * Handle view changes - re-apply surface isolation when switching views.
   * Non-camera work only; camera alignment happens in handleViewSettled,
   * after the ENTIRE switch pipeline (and any queued follow-up switch) has
   * landed — so the tool's camera write is last by ordering, not by timing.
   */
  private handleViewChanged = (): void => {
    // Re-apply surface context if we have surface data
    if (this.currentSurfacePlane) {
      this.updateSurfaceIsolation(this.currentSurfacePlane);
      this.onViewChanged();
    }
  };

  /**
   * Camera alignment on VIEW_SETTLED: entering 2D aligns orientation to the
   * surface and restores the saved 2D position (when returning from 3D),
   * keeps the camera uncentered when already aligned (tool switches), and
   * centers on first alignment. Replaces the old setTimeout(0) deferral
   * that raced the ViewManager's async pipeline.
   */
  private handleViewSettled = (): void => {
    if (!this.enabled || !this.currentSurfacePlane) return;
    if (this.viewer.scene.cameraMode !== CameraMode.ORTHOGRAPHIC) return;

    const savedState = this.surfacePersona.getSaved2DCameraState();
    const alreadyAligned = this.surfacePersona.hasAlignedToCurrentSurface();

    if (savedState) {
      // Restore saved 2D camera state (position + zoom)
      // Align orientation but don't center - we'll restore saved position
      this.alignViewToSurface(this.currentSurfacePlane, false);
      const view = this.viewer.scene.view;
      view.position.x = savedState.x;
      view.position.y = savedState.y;
      view.position.z = savedState.z;
      view.radius = savedState.radius;
    } else if (alreadyAligned) {
      // Already aligned to this surface (switching between surface tools)
      // Only update orientation, don't center the camera
      this.alignViewToSurface(this.currentSurfacePlane, false);
    } else {
      // First time aligning to this surface - align orientation and center
      this.alignViewToSurface(this.currentSurfacePlane, true);
    }

    this.surfacePersona.setAlignedToCurrentSurface(true);
  };

  /**
   * Subscribe to tolerance property changes for the current surface
   */
  private subscribeToToleranceProperty(surfaceIdentifier: string): void {
    // Unsubscribe from previous if any
    this.unsubscribeFromToleranceProperty();

    try {
      const service = getGlobalPropertyService();
      const toleranceProperty = service.getProperty('surface_tolerance', surfaceIdentifier, PropertyType.PROJECT_GLOBAL);

      // Subscribe to changes - automatically updates when changed from React UI or anywhere else
      this.toleranceSubscription = toleranceProperty.subscribe((tolerance) => {
        if (this.currentSurface && typeof tolerance === 'number') {
          // Update the surface object
          this.currentSurface.tolerance = tolerance;

          // Re-apply surface isolation with new tolerance
          if (this.currentSurfacePlane) {
            this.updateSurfaceIsolation(this.currentSurfacePlane);
          }
        }
      });
    } catch (error) {
      console.warn('Failed to subscribe to tolerance property:', error);
      // Fallback to default behavior without reactive properties
    }
  }

  /**
   * Unsubscribe from tolerance property
   */
  private unsubscribeFromToleranceProperty(): void {
    if (this.toleranceSubscription) {
      this.toleranceSubscription.unsubscribe();
      this.toleranceSubscription = null;
    }
  }

  /**
   * Subscribe to rotation property changes for the current surface
   * When rotation changes (e.g., from UI), update the camera roll if in 2D mode
   */
  private subscribeToRotationProperty(surfaceIdentifier: string): void {
    // Unsubscribe from previous if any
    this.unsubscribeFromRotationProperty();

    try {
      const rotationProperty = getSurfacePlaneRotationProperty(surfaceIdentifier);

      // Subscribe to changes - automatically updates camera roll when changed from React UI
      this.rotationSubscription = rotationProperty.subscribe((rotation) => {
        if (typeof rotation !== 'number') return;
        if (this.viewer.scene.cameraMode === CameraMode.ORTHOGRAPHIC) {
          // Update camera roll to match surface rotation
          this.viewer.scene.view.roll = rotation;
        }
        // Also sync the widget's roll so its orientation tracks the surface
        const widgetState = this.getWidgetViewState();
        widgetState?.setRoll(rotation);
      });
    } catch (error) {
      console.warn('Failed to subscribe to rotation property:', error);
    }
  }

  /**
   * Unsubscribe from rotation property
   */
  private unsubscribeFromRotationProperty(): void {
    if (this.rotationSubscription) {
      this.rotationSubscription.unsubscribe();
      this.rotationSubscription = null;
    }
  }

  /**
   * Handle selection changes
   */
  private handleSelectionChanged = (event: { selectedSurfaceIdentifier: string | null; surface: PointoramaSurface | null }): void => {
    const selectedSurfaceIdentifier = event.selectedSurfaceIdentifier;
    const surface = event.surface as PointoramaSurface | null;

    // Skip if the same surface is already selected
    if (selectedSurfaceIdentifier === this.currentSurfaceIdentifier) {
      return;
    }

    // Always clear the old isolation plane first when changing selection
    this.clearSurfaceIsolationPlane();
    this.unsubscribeFromToleranceProperty();
    this.unsubscribeFromRotationProperty();

    if (selectedSurfaceIdentifier && surface) {
      this.currentSurfaceIdentifier = selectedSurfaceIdentifier;
      this.currentSurface = surface;
      this.currentSurfacePlane = surface.getPlane();

      // Set up isolation and view alignment
      // Note: alignment flag is reset in SurfacePersona when surface changes
      this.updateSurfaceIsolation(surface.getPlane());
      // Align after any in-flight view switch lands (ordering, not timing —
      // replaces the old setTimeout(0) racing the switch pipeline).
      void this.viewer.personaManager?.viewManager.whenSettled().then(() => {
        if (this.enabled && this.currentSurfacePlane && !this.surfacePersona.hasAlignedToCurrentSurface()) {
          this.alignViewToSurface(this.currentSurfacePlane);
          this.surfacePersona.setAlignedToCurrentSurface(true);
        }
      });
      // Subscribe to tolerance property for the new surface
      this.subscribeToToleranceProperty(selectedSurfaceIdentifier);

      // Subscribe to rotation property (syncs camera roll in 2D mode)
      this.subscribeToRotationProperty(selectedSurfaceIdentifier);

      // Re-attempt widget-view subscription — the widget may have been
      // created (first user open) after the tool was enabled.
      if (!this.widgetViewIdSubscription) {
        this.subscribeToWidgetViewId();
      }

      // Notify subclass about surface change
      this.onSurfaceChanged(surface, selectedSurfaceIdentifier);
    } else {
      // No surface selected
      this.currentSurface = null;
      this.currentSurfacePlane = null;
      this.currentSurfaceIdentifier = null;

      // Notify subclass about surface cleared
      this.onSurfaceChanged(null, null);
    }
  };

  /**
   * Update the surface isolation plane
   * Creates a clipping plane that shows only points near the surface
   */
  protected updateSurfaceIsolation(plane: SurfacePlane): void {
    // Clear existing isolation plane
    this.clearSurfaceIsolationPlane();

    // Calculate the bounding box of the point cloud for sizing the isolation plane
    const boundingBox = this.viewer.getBoundingBox(this.viewer.scene.pointclouds);
    const size = boundingBox.getSize(new THREE.Vector3());
    const radius = Math.max(size.x, size.y, size.z) / 2;

    // Get tolerance from the scene surface (which gets updated by property sync), fallback to default
    const sceneSurface = this.currentSurfaceIdentifier ? this.viewer.scene.surfaces?.find((s: PointoramaSurface) => s.identifier === this.currentSurfaceIdentifier) : null;
    const tolerance = sceneSurface?.tolerance ?? this.currentSurface?.tolerance ?? SurfaceToolBase.DEFAULT_ISOLATION_TOLERANCE;

    // Create clipping plane aligned to the surface
    const isolationPlane = new PointoramaPlane({
      position: plane.position.clone(),
      normal: plane.normal.clone(),
      clipTask: ClipTask.SHOW_INSIDE,
      tolerance: tolerance,
      radius: radius,
      selected: false,
      finished: false,
    });

    // Add plane to scene
    this.viewer.scene.addPlane(isolationPlane);
    this.surfaceIsolationPlane = isolationPlane;
  }

  /**
   * Clear the surface isolation plane
   */
  protected clearSurfaceIsolationPlane(): void {
    if (this.surfaceIsolationPlane) {
      this.viewer.scene.removePlane(this.surfaceIsolationPlane);
      this.surfaceIsolationPlane = null;
    }
  }

  /**
   * Align the camera to look perpendicular to the surface plane and center on it
   * This sets up the camera pitch/yaw/roll for a 2D orthographic view aligned with the surface
   * and positions the camera to look at the surface center
   *
   * @param plane - The surface plane to align to
   * @param centerOnSurface - Whether to center the camera on the surface (default: true)
   */
  protected alignViewToSurface(plane: SurfacePlane, centerOnSurface = true): void {
    // Hide map if visible
    this.viewer.hideMap();

    // Clone and normalize the normal vector to ensure it's a proper unit vector
    const normalizedNormal = plane.normal.clone().normalize();

    // Create a PointoramaPlane for view alignment (needed for camera orientation)
    const viewPlane = new PointoramaPlane({
      position: plane.position.clone(),
      normal: normalizedNormal,
      clipTask: ClipTask.NONE,
    });

    // Ensure inclination and bearing are calculated with the correct normal
    // This is a safety measure in case the constructor didn't calculate them correctly
    viewPlane.setRotationFromNormal(normalizedNormal);
    viewPlane.calculateInclinationAndBearing();

    // Store the plane in viewerMode for 2D mode
    this.viewer.viewerMode.in2DMode = true;
    this.viewer.viewerMode.associatedPlane = viewPlane;
    this.viewer.viewerMode.hasVirtualPlane = true;

    // Set camera to orthographic mode.
    // EDL was already suppressed by ViewManager.applyViewContext when the surface
    // view was entered; no need to touch it here.
    this.viewer.setCameraMode(CameraMode.ORTHOGRAPHIC);

    // Align camera orientation to look perpendicular to the surface
    const view = this.viewer.scene.view;
    view.pitch = viewPlane.inclination - Math.PI / 2;

    // When looking straight down (inclination near 0) or straight up (inclination near π),
    // the bearing becomes meaningless (gimbal lock). In these cases, set yaw to 0 for
    // consistent, predictable orientation where north (Y+) aligns with screen up.
    const VERTICAL_THRESHOLD = 0.01; // ~0.57 degrees
    const isLookingDown = viewPlane.inclination < VERTICAL_THRESHOLD;
    const isLookingUp = viewPlane.inclination > Math.PI - VERTICAL_THRESHOLD;

    if (isLookingDown || isLookingUp) {
      view.yaw = 0;
    } else {
      view.yaw = 2 * Math.PI - viewPlane.bearing;
    }

    // Use the surface's normalRotation for camera roll
    view.roll = this.currentSurface?.normalRotation ?? 0;

    // Center camera on surface position if requested
    if (centerOnSurface) {
      view.position.copy(plane.position);
    }

    // Mirror the same orientation onto the widget view so its xray render
    // tracks the surface just like the main view does.
    const widgetState = this.getWidgetViewState();
    widgetState?.alignToSurfaceOrientation(
      viewPlane.inclination,
      viewPlane.bearing,
      this.currentSurface?.normalRotation ?? 0,
      plane.position,
      centerOnSurface,
    );
  }

  /**
   * Locate the widget's RenderViewState (lives on `viewer.renderViews[1+]`).
   * Returns the state only when the widget is currently showing the surface
   * xray view — the widget tracks its own view independently of the main
   * view, and we must not clobber the user's widget-side view choice (e.g.
   * a 3D perspective view) just because the main view aligned to a surface.
   * Returns null before the widget has been ensured for this project.
   */
  private getWidgetViewState(): RenderViewState | null {
    const views = this.viewer.renderViews;
    for (let i = 1; i < views.length; i++) {
      const vs = views[i].viewState;
      if (!vs) continue;
      if (vs.currentViewId.value !== 'xray_2d_surface') return null;
      return vs;
    }
    return null;
  }

  /**
   * Raw lookup of the widget's RenderViewState — does NOT gate on the
   * widget's current view id. Used by infra that needs to attach to the
   * widget regardless of which view it's currently showing (e.g. the
   * subscription that fires when the widget enters the surface view).
   */
  private getRawWidgetViewState(): RenderViewState | null {
    const views = this.viewer.renderViews;
    for (let i = 1; i < views.length; i++) {
      const vs = views[i].viewState;
      if (vs) return vs;
    }
    return null;
  }

  /**
   * Subscribe to the widget's `currentViewId`. When it transitions into the
   * surface xray view, re-apply the current surface alignment so the
   * widget's camera matches the main view. Without this hook, the widget's
   * own `applyView` (called from its view switcher) pins the camera to the
   * view's declared default pose — which for `xray_2d_surface` is the
   * generic ortho-top framing, ignoring the selected surface entirely.
   */
  private subscribeToWidgetViewId(): void {
    this.unsubscribeFromWidgetViewId();
    const widgetState = this.getRawWidgetViewState();
    if (!widgetState) return;
    this.widgetViewIdSubscription = widgetState.currentViewId.subscribe((viewId) => {
      if (viewId !== 'xray_2d_surface') return;
      if (!this.enabled || !this.currentSurfacePlane) return;
      // Defer to next frame: `setView` is mid-flight when the subscription
      // fires, and applyView's async transition may still write the pose
      // after this callback returns. A microtask delay lands the alignment
      // after applyView's last pose write.
      setTimeout(() => {
        if (!this.enabled || !this.currentSurfacePlane) return;
        if (widgetState.currentViewId.value !== 'xray_2d_surface') return;
        this.alignWidgetToCurrentSurface(widgetState);
      }, 0);
    });
  }

  private unsubscribeFromWidgetViewId(): void {
    if (this.widgetViewIdSubscription) {
      this.widgetViewIdSubscription.unsubscribe();
      this.widgetViewIdSubscription = null;
    }
  }

  /**
   * Apply the current surface's orientation to the given widget view.
   * Centralizes the plane-normalization + alignToSurfaceOrientation call
   * so both the widget-view-switched and main-view-aligned paths use the
   * same math.
   */
  private alignWidgetToCurrentSurface(widgetState: RenderViewState): void {
    const plane = this.currentSurfacePlane;
    if (!plane) return;
    const normalizedNormal = plane.normal.clone().normalize();
    const viewPlane = new PointoramaPlane({
      position: plane.position.clone(),
      normal: normalizedNormal,
      clipTask: ClipTask.NONE,
    });
    viewPlane.setRotationFromNormal(normalizedNormal);
    viewPlane.calculateInclinationAndBearing();
    widgetState.alignToSurfaceOrientation(
      viewPlane.inclination,
      viewPlane.bearing,
      this.currentSurface?.normalRotation ?? 0,
      plane.position,
      true,
    );
  }

  /**
   * Hook for subclasses to react to surface selection changes
   * Override this in subclasses to add custom behavior when surface changes
   *
   * @param surface - The selected surface (null if cleared)
   * @param identifier - The surface identifier (null if cleared)
   */
  protected onSurfaceChanged(surface: PointoramaSurface | null, identifier: string | null): void {
    // Default: do nothing
    // Subclasses can override to react to surface changes
  }

  /**
   * Hook for subclasses to react to view changes
   * Override this in subclasses to add custom behavior when view mode changes
   */
  protected onViewChanged(): void {
    // Default: do nothing
    // Subclasses can override to react to view changes
  }

  /**
   * Get the current surface plane data
   */
  protected getSurfacePlane(): SurfacePlane | null {
    return this.currentSurfacePlane;
  }

  /**
   * Get the isolation plane (for subclasses that need to modify it)
   */
  protected getIsolationPlane(): PointoramaPlane | null {
    return this.surfaceIsolationPlane;
  }

  // ========== Camera Alignment Tool Integration ==========

  /** Track mouse down position for alignment drag detection */
  private alignmentMouseDownPos: THREE.Vector2 | null = null;

  /** Threshold for drag detection */
  private readonly ALIGNMENT_DRAG_THRESHOLD = 5;

  /** Whether waiting for second click in click-click mode */
  private alignmentWaitingForSecondClick = false;

  /** Store the screen position where alignment started (for accurate angle calculation) */
  private alignmentStartScreenPos: THREE.Vector2 | null = null;

  /**
   * Convert screen coordinates to world position on the surface plane
   */
  protected screenToWorldOnSurface(screen: THREE.Vector2): THREE.Vector3 | null {
    const camera = this.viewer.scene.getActiveCamera();
    const viewportWidth = this.viewer.renderArea.clientWidth;
    const viewportHeight = this.viewer.renderArea.clientHeight;

    const ndcX = (screen.x / viewportWidth) * 2 - 1;
    const ndcY = -(screen.y / viewportHeight) * 2 + 1;

    // Unproject to get ray
    const nearPoint = new THREE.Vector3(ndcX, ndcY, -1).unproject(camera);
    const farPoint = new THREE.Vector3(ndcX, ndcY, 1).unproject(camera);
    const rayDirection = new THREE.Vector3().subVectors(farPoint, nearPoint).normalize();
    const ray = new THREE.Ray(nearPoint, rayDirection);

    // If we have a surface plane, intersect with it
    if (this.currentSurfacePlane) {
      const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(this.currentSurfacePlane.normal, this.currentSurfacePlane.position);
      const intersection = ray.intersectPlane(plane, new THREE.Vector3());
      if (intersection) {
        return intersection;
      }
    }

    // Fallback: intersect with the associated plane from viewerMode
    const associatedPlane = this.viewer.viewerMode.associatedPlane;
    if (associatedPlane) {
      const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(associatedPlane.getNormal(), associatedPlane.position.clone());
      const intersection = ray.intersectPlane(plane, new THREE.Vector3());
      if (intersection) {
        return intersection;
      }
    }

    return null;
  }

  /**
   * Handle camera alignment mouse down - records position for click vs drag detection
   * Should be called from subclass onMouseDown in 2D mode
   * Returns true only if alignment is already in progress (to block other interactions)
   */
  protected handleAlignmentMouseDown(event: MouseEvent): boolean {
    // Only handle in 2D mode
    if (this.viewer.scene.cameraMode !== CameraMode.ORTHOGRAPHIC) return false;
    if (event.button !== 0) return false; // Left click only

    // Record mouse down position for drag detection
    const rect = this.viewer.renderArea.getBoundingClientRect();
    this.alignmentMouseDownPos = new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);

    // If alignment is already active or waiting for second click, block other interactions
    if (this.cameraAlignmentTool.isAlignmentActive() || this.alignmentWaitingForSecondClick) {
      return true;
    }

    // First potential click - don't block yet, let subclass set up its state (e.g. areaSelectionStart)
    // We'll handle it on mouse up based on whether it was a click or drag
    return false;
  }

  /**
   * Handle camera alignment mouse move
   * Should be called from subclass onMouseMove
   * Returns true if alignment was updated (subclass should skip normal handling)
   */
  protected handleAlignmentMouseMove(event: MouseEvent): boolean {
    if (!this.cameraAlignmentTool.isAlignmentActive()) return false;

    const rect = this.viewer.renderArea.getBoundingClientRect();
    const screenPos = new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
    const worldPos = this.screenToWorldOnSurface(screenPos);

    if (worldPos) {
      this.cameraAlignmentTool.updateEndPoint(worldPos, screenPos);
    }

    return true;
  }

  /**
   * Handle camera alignment mouse up
   * Should be called from subclass onMouseUp
   * Starts alignment on click-without-drag (when not doing other interactions)
   * Returns true if alignment was completed/handled (subclass should skip normal handling)
   */
  protected handleAlignmentMouseUp(event: MouseEvent): boolean {
    // Only handle in 2D mode
    if (this.viewer.scene.cameraMode !== CameraMode.ORTHOGRAPHIC) {
      this.alignmentMouseDownPos = null;
      return false;
    }

    if (event.button !== 0) return false;

    const rect = this.viewer.renderArea.getBoundingClientRect();
    const mouse = new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);

    // Check if this was a drag or click
    const isDrag = this.alignmentMouseDownPos && this.isAlignmentDrag(this.alignmentMouseDownPos, mouse);

    if (this.alignmentWaitingForSecondClick) {
      // Second click - complete alignment
      const worldPos = this.screenToWorldOnSurface(mouse);
      if (worldPos) {
        this.completeCameraAlignment(worldPos, mouse);
      }
      this.alignmentWaitingForSecondClick = false;
      this.alignmentMouseDownPos = null;
      return true;
    } else if (this.cameraAlignmentTool.isAlignmentActive() && isDrag) {
      // Drag mode while alignment active - complete alignment
      const worldPos = this.screenToWorldOnSurface(mouse);
      if (worldPos) {
        this.completeCameraAlignment(worldPos, mouse);
      }
      this.alignmentMouseDownPos = null;
      return true;
    } else if (this.alignmentMouseDownPos && !isDrag) {
      // Click without drag - start camera alignment and wait for second click
      const worldPos = this.screenToWorldOnSurface(mouse);
      if (worldPos) {
        if (!this.cameraAlignmentTool.isAlignmentActive()) {
          // First click - start alignment
          this.cameraAlignmentTool.start(worldPos, mouse);
          this.alignmentStartScreenPos = mouse.clone();
        }
        // Set waiting for second click (whether we just started or were already active)
        this.alignmentWaitingForSecondClick = true;
      }
      this.alignmentMouseDownPos = null;
      // Return false to let subclass clean up its state (e.g. areaSelectionStart)
      // The alignment is started but we want the "click without drag - clean up" code to run
      return false;
    }

    this.alignmentMouseDownPos = null;
    return false;
  }

  /**
   * Handle keyboard events for alignment (ESC to cancel)
   * Returns true if handled
   */
  protected handleAlignmentKeyDown(event: KeyboardEvent): boolean {
    if (event.key === 'Escape') {
      if (this.cameraAlignmentTool.isAlignmentActive() || this.alignmentWaitingForSecondClick) {
        this.cameraAlignmentTool.cancel();
        this.alignmentWaitingForSecondClick = false;
        return true;
      }
    }
    return false;
  }

  /**
   * Check if mouse moved significantly for drag detection
   */
  private isAlignmentDrag(start: THREE.Vector2, end: THREE.Vector2): boolean {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    return Math.sqrt(dx * dx + dy * dy) > this.ALIGNMENT_DRAG_THRESHOLD;
  }

  /**
   * Complete camera alignment and update surface rotation
   * This sets the camera roll and saves the rotation to the surface's normalRotation property
   * Also persists the rotation via the reactive property system for database storage
   * @param endWorldPosition - The world position of the end point
   * @param endScreenPosition - The screen position in pixels (Y down)
   */
  protected completeCameraAlignment(endWorldPosition: THREE.Vector3, endScreenPosition: THREE.Vector2): void {
    if (!this.cameraAlignmentTool.isAlignmentActive()) return;

    const startPoint = this.cameraAlignmentTool.getStartPoint();
    if (!startPoint || !this.alignmentStartScreenPos) return;

    // Calculate angle directly from screen pixel coordinates (Y down)
    // This works for any camera orientation
    const screenDx = endScreenPosition.x - this.alignmentStartScreenPos.x;
    const screenDy = endScreenPosition.y - this.alignmentStartScreenPos.y;

    // In screen pixels (Y down): positive angle = clockwise from right
    const screenAngle = Math.atan2(screenDy, screenDx);

    // The angle difference from horizontal (screen right = 0 radians)
    let angleDiff = screenAngle;
    while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
    while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

    // Add this angle difference to the current roll
    // In screen pixel space (Y down), positive angle is clockwise
    // Roll works such that positive roll rotates the view counter-clockwise
    // So to align a line with horizontal, we add the angle
    const currentRoll = this.viewer.scene.view.roll;
    let newRoll = currentRoll + angleDiff;
    while (newRoll > Math.PI) newRoll -= 2 * Math.PI;
    while (newRoll < -Math.PI) newRoll += 2 * Math.PI;

    // Set the camera roll
    this.viewer.scene.view.roll = newRoll;

    // Update the surface's normalRotation property (in-memory)
    if (this.currentSurface) {
      this.currentSurface.normalRotation = newRoll;
    }

    // Persist the rotation via the reactive property system
    if (this.currentSurfaceIdentifier) {
      try {
        const rotationProperty = getSurfacePlaneRotationProperty(this.currentSurfaceIdentifier);
        rotationProperty.value = newRoll;
      } catch (error) {
        console.warn('Failed to persist surface rotation:', error);
      }
    }

    // Cancel the alignment tool visuals and clear state
    this.cameraAlignmentTool.cancel();
    this.alignmentStartScreenPos = null;
  }

  /**
   * Cancel camera alignment mode
   */
  protected cancelCameraAlignment(): void {
    this.cameraAlignmentTool.cancel();
    this.alignmentWaitingForSecondClick = false;
    this.alignmentStartScreenPos = null;
  }

  /**
   * Check if camera alignment is active
   */
  protected isCameraAlignmentActive(): boolean {
    return this.cameraAlignmentTool.isAlignmentActive() || this.alignmentWaitingForSecondClick;
  }

  /**
   * Get the camera alignment tool instance (for subclasses that need direct access)
   */
  protected getCameraAlignmentTool(): CameraAlignmentTool {
    return this.cameraAlignmentTool;
  }
}
